import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, X } from 'lucide-react';

import { usePwaUpdateStore } from '../../stores/pwaUpdateStore';
import { applyUpdate } from '../../utils/pwa';

/**
 * Hybrid update behaviour:
 *  - On any normal screen, a newly-downloaded build is applied silently
 *    (a quick reload onto the fresh version).
 *  - While an inspection is in progress, a surprise reload could interrupt
 *    live work, so we DON'T auto-apply there. We show a small banner and let
 *    the build apply when the inspector leaves/finishes the inspection (or
 *    immediately if they tap "Update now").
 *
 * Mounted once at the app root (RootLayout).
 */
export function UpdatePrompt() {
  const updateReady = usePwaUpdateStore((s) => s.updateReady);
  const location = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const insideInspection = location.pathname.startsWith('/lite/inspect');

  useEffect(() => {
    // Safe screen (incl. the moment they leave an inspection) -> apply now.
    if (updateReady && !insideInspection) {
      applyUpdate();
    }
  }, [updateReady, insideInspection]);

  if (!updateReady || !insideInspection || dismissed) return null;

  return (
    <div className="fixed bottom-24 inset-x-0 z-[60] flex justify-center px-4 pointer-events-none">
      <div className="pointer-events-auto flex items-center gap-3 rounded-full bg-slate-900/95 dark:bg-slate-700 text-white shadow-lg pl-4 pr-2 py-2 max-w-sm">
        <RefreshCw className="w-4 h-4 flex-shrink-0 text-primary-300" />
        <span className="text-sm">
          New version ready — updates when you finish.
        </span>
        <button
          onClick={() => applyUpdate()}
          className="flex-shrink-0 text-xs font-semibold bg-primary-500 hover:bg-primary-400 text-white rounded-full px-3 py-1.5"
        >
          Update now
        </button>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="flex-shrink-0 p-1 text-slate-300 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
